import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { PageLoader } from '@/components/layout/PageLoader';
import { env } from '@/lib/env';
import { useAccountStore } from '@/store/account.store';
import { useReadersStore } from '@/store/readers.store';
import { useSessionStore } from '@/store/session.store';

/**
 * Logout — `/logout`. A standalone sign-out route reachable on any device:
 * it's PUBLIC (registered outside `ProtectedRoute`), so neither the auth
 * gate nor the mobile gate intercepts it. Navigating here clears the
 * persisted stores and hands off to Auth0's logout, which redirects back to
 * the app origin → `/` → `/login`.
 *
 * Mirrors the Account page "Sign out" action so both paths behave
 * identically — handy as a shareable/bookmarkable sign-out link, and for the
 * native apps to punch a user out of their web browser session.
 */
export function LogoutPage() {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const resetAccount = useAccountStore((s) => s.reset);
  const resetReaders = useReadersStore((s) => s.reset);
  const resetSession = useSessionStore((s) => s.reset);
  // Fire exactly once — guards against React StrictMode's double-mount in dev
  // and any re-render from the Auth0 SDK handing back a new `logout` ref.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    // Order matters: clear local state before handing off so no subscriber
    // re-renders against a half-cleared session (mirrors Account → Sign out).
    resetReaders();
    resetAccount();
    resetSession();

    if (env.isAuth0Configured) {
      logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
      // No Auth0 (local scaffolding) — nothing to revoke; just bounce to login.
      navigate('/login', { replace: true });
    }
  }, [logout, navigate, resetAccount, resetReaders, resetSession]);

  return <PageLoader label="Signing you out…" />;
}
